import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { LegalMarkdown } from "@/pages/landing/LegalMarkdown";
import { errText } from "./useLegalAdmin";

const BTN = "border-2 border-black px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40";

const toLines = (rows) => (rows || []).map((r) => (r.location ? `${r.name} | ${r.location}` : r.name)).join("\n");
const fromLines = (text) => text.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
  const [name, ...rest] = l.split("|");
  return { name: name.trim(), location: rest.join("|").trim() };
}).filter((r) => r.name);

function sourceLabel(doc, pageId) {
  if (doc.is_custom) return pageId ? "Custom for this buyer" : "Edited";
  return pageId ? "Using the shared version" : "Built-in starting text";
}

export const LegalDocEditor = ({ doc, pageId, canEdit, onSaved }) => {
  const [text, setText] = useState(doc.markdown);
  const [extra, setExtra] = useState(toLines(doc.extra_partners));
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(doc.markdown); setExtra(toLines(doc.extra_partners)); }, [doc]);
  const showExtra = doc.kind === "partners" && !pageId;
  const dirty = text !== doc.markdown || (showExtra && extra !== toLines(doc.extra_partners));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/legal/doc", {
        kind: doc.kind, page_id: pageId || null, markdown: text,
        ...(showExtra ? { extra_partners: fromLines(extra) } : {}),
      });
      toast.success(`${doc.title} saved`);
      onSaved();
    } catch (e) { toast.error(errText(e, "Couldn't save")); } finally { setBusy(false); }
  };
  const reset = async () => {
    const msg = pageId ? "Stop using this buyer's own version and go back to the shared one?" : "Replace your edits with the built-in starting text?";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.delete("/admin/legal/doc", { params: { kind: doc.kind, ...(pageId ? { page_id: pageId } : {}) } });
      toast.success("Reset");
      onSaved();
    } catch (e) { toast.error(errText(e, "Couldn't reset")); } finally { setBusy(false); }
  };

  return (
    <div data-testid={`legal-editor-${doc.kind}`}>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="border border-black px-1.5 py-0.5 text-[9px] uppercase tracking-widest" data-testid={`legal-editor-source-${doc.kind}`}>{sourceLabel(doc, pageId)}</span>
        {doc.updated_at && <span className="text-[10px] text-zinc-500">Saved {new Date(doc.updated_at).toLocaleString()} {doc.updated_by_email ? `by ${doc.updated_by_email}` : ""}</span>}
        <button type="button" className={`${BTN} ml-auto ${preview ? "bg-white" : "bg-black text-white"}`} onClick={() => setPreview(false)} data-testid={`legal-editor-edit-tab-${doc.kind}`}>Edit</button>
        <button type="button" className={`${BTN} ${preview ? "bg-black text-white" : "bg-white"}`} onClick={() => setPreview(true)} data-testid={`legal-editor-preview-tab-${doc.kind}`}>Preview</button>
      </div>
      {preview ? (
        <>
        <div className="text-[10px] text-zinc-500 mb-1">Fill-in words like {"{{firm}}"} are filled in on the live page — use View live page to see the final text.</div>
        <div className="border-2 border-black p-4 max-h-[420px] overflow-y-auto prose-sm text-sm [&_h2]:font-bold [&_h2]:mt-3 [&_ul]:list-disc [&_ul]:pl-5 [&_p]:mb-2" data-testid={`legal-editor-preview-${doc.kind}`}>
          <LegalMarkdown text={text} />
        </div>
        </>
      ) : (
        <textarea value={text} onChange={(e) => setText(e.target.value)} disabled={!canEdit} rows={14}
                  className="w-full border-2 border-black p-3 font-mono text-[12px] leading-relaxed bg-white disabled:bg-zinc-50"
                  data-testid={`legal-editor-text-${doc.kind}`} />
      )}
      {showExtra && (
        <label className="block mt-3">
          <span className="label-mono">Extra partners not set up as buyers — one per line: Name | City, State</span>
          <textarea value={extra} onChange={(e) => setExtra(e.target.value)} disabled={!canEdit} rows={4}
                    className="w-full border-2 border-black p-2 font-mono text-[12px] mt-1 bg-white disabled:bg-zinc-50"
                    data-testid="legal-editor-extra-partners" />
          <span className="text-[10px] text-zinc-500">Live buyers are listed automatically (turn this off per buyer in their Legal button).</span>
        </label>
      )}
      {canEdit && (
        <div className="flex gap-2 mt-3 justify-end">
          {doc.is_custom && <button type="button" className={`${BTN} bg-white`} onClick={reset} disabled={busy} data-testid={`legal-editor-reset-${doc.kind}`}>{pageId ? "Use shared version" : "Reset to starting text"}</button>}
          <button type="button" className={`${BTN} bg-black text-white`} onClick={save} disabled={busy || !dirty || !text.trim()} data-testid={`legal-editor-save-${doc.kind}`}>
            {busy ? "Saving…" : pageId && !doc.is_custom ? "Save as this buyer's version" : "Save"}
          </button>
        </div>
      )}
    </div>
  );
};
